import { useState } from "react";
import { FileText } from "lucide-react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { toastApiError } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type CompanyInboxBillResponse = components["schemas"]["CompanyInboxBillResponse"];

interface ViewCompanyBillDocumentButtonProps {
  companyId: string;
  bill: CompanyInboxBillResponse | null;
  variant?: "outline" | "ghost" | "secondary";
  size?: "sm" | "default";
  label?: string;
  className?: string;
}

/** Opens the document of a bill still in the company inbox. The short-lived URL is fetched per click, not per row. */
export function ViewCompanyBillDocumentButton({
  companyId,
  bill,
  variant = "outline",
  size = "sm",
  label = "View",
  className,
}: ViewCompanyBillDocumentButtonProps) {
  const [loading, setLoading] = useState(false);

  // A bill can genuinely have no document; disabled with a reason, since a missing button reads as a bug.
  const hasDocument = Boolean(bill?.fileName);

  async function handleView() {
    if (!bill?.id || loading) return;

    // Opened on the click itself: browsers (Safari) block window.open after an await. No "noopener", which makes
    // window.open return null; the opener is severed below instead.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    setLoading(true);
    try {
      const { data, error, response } = await api.GET(
        "/v1/companies/{companyId}/bills/{billId}/file-url",
        { params: { path: { companyId, billId: bill.id } } },
      );

      if (error || !data?.url) {
        tab?.close();
        await toastApiError({ error, response }, "Couldn't open the document.");
        return;
      }

      if (tab) tab.location.replace(data.url);
      // Blocked anyway: ask again now there's a real URL, rather than fail silently.
      else window.open(data.url, "_blank", "noopener,noreferrer");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={className}
      disabled={!hasDocument || loading}
      onClick={handleView}
      title={hasDocument ? bill?.fileName ?? undefined : "No document was attached to this bill"}
    >
      {loading ? <Spinner className="size-3.5" /> : <FileText className="size-3.5" />}
      {label}
    </Button>
  );
}
